import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  fetchAdminLayouts,
  fetchAdminLayoutDetail,
  exportLayout,
  softDeleteLayout,
  restoreLayout,
  forceUnpublishLayout,
  type LayoutListParams,
} from '@/lib/api/admin-layouts'
import { downloadJson } from '@/lib/download'
import { buildExportFileName } from '@/lib/exportFileName'

export const adminLayoutKeys = {
  all: ['admin-layouts'] as const,
  list: (params: LayoutListParams) => [...adminLayoutKeys.all, 'list', params] as const,
  detail: (layoutId: string | undefined) => [...adminLayoutKeys.all, 'detail', layoutId] as const,
}

export function useAdminLayouts(params: LayoutListParams = {}) {
  return useQuery({
    queryKey: adminLayoutKeys.list(params),
    queryFn: () => fetchAdminLayouts(params),
    staleTime: 60 * 1000,
  })
}

export function useAdminLayoutDetail(layoutId: string | undefined) {
  return useQuery({
    queryKey: adminLayoutKeys.detail(layoutId),
    queryFn: () => fetchAdminLayoutDetail(layoutId!),
    enabled: !!layoutId,
  })
}

/**
 * Downloads the layout + parent-location JSON bundle. The server names nothing —
 * the filename is built client-side from the bundle it just returned.
 */
export function useExportLayout() {
  return useMutation({
    mutationFn: (layoutId: string) => exportLayout(layoutId),
    onSuccess: (bundle) => {
      const layout = bundle.layouts[0] ?? {}
      downloadJson(
        bundle,
        buildExportFileName(
          'layout',
          String(layout.name ?? 'layout'),
          String(layout.id ?? ''),
          new Date(),
        ),
      )
      toast.success('Layout exported')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Export failed'),
  })
}

export function useSoftDeleteLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ layoutId, reason }: { layoutId: string; reason: string }) =>
      softDeleteLayout(layoutId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLayoutKeys.all })
      toast.success('Layout soft-deleted')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete layout'),
  })
}

export function useRestoreLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ layoutId, reason }: { layoutId: string; reason: string }) =>
      restoreLayout(layoutId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLayoutKeys.all })
      toast.success('Layout restored')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to restore layout'),
  })
}

export function useForceUnpublishLayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ layoutId, reason }: { layoutId: string; reason: string }) =>
      forceUnpublishLayout(layoutId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminLayoutKeys.all })
      toast.success('Layout unpublished')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to unpublish layout'),
  })
}
